import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send, Loader2, RefreshCw, Archive, Trash2 } from "lucide-react";
import { ArchivedMessagesTab } from "./ArchivedMessagesTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// POLL_MS: how often to check for new messages while the inbox is open
const POLL_MS = 30_000;

export const ParentDirectorInbox = ({ onUnreadCount }) => {
  const [messages, setMessages] = useState([]);
  const [archived, setArchived] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState({});
  const [replying, setReplying] = useState(null);
  const pollRef = useRef(null);

  const fetchMessages = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [activeRes, archivedRes] = await Promise.all([
        axios.get(`${API}/parent-communication/director/messages`),
        axios.get(`${API}/parent-communication/director/messages/archived`),
      ]);
      setMessages(activeRes.data.messages || []);
      setArchived(archivedRes.data.messages || []);
      onUnreadCount?.(0);
    } catch {
      if (!silent) toast.error("Unable to load messages");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(() => fetchMessages(true), POLL_MS);
    return () => clearInterval(pollRef.current);
  }, []); // eslint-disable-line

  const sendReply = async (messageId) => {
    const text = (replyText[messageId] || "").trim();
    if (!text) return;
    setReplying(messageId);
    try {
      await axios.post(`${API}/parent-communication/director/messages/${messageId}/reply`, { reply: text });
      toast.success("Reply sent");
      setReplyText((prev) => ({ ...prev, [messageId]: "" }));
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to send reply");
    } finally {
      setReplying(null);
    }
  };

  const archiveMessage = async (msg) => {
    try {
      await axios.patch(`${API}/parent-communication/director/messages/${msg.id}/archive`);
      toast.success("Message archived");
      fetchMessages();
    } catch {
      toast.error("Unable to archive message");
    }
  };

  const deleteMessage = async (msg) => {
    if (!window.confirm(`Permanently delete this message from ${msg.parentName}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/parent-communication/director/messages/${msg.id}`);
      toast.success("Message deleted");
      fetchMessages();
    } catch {
      toast.error("Unable to delete message");
    }
  };

  const restoreMessage = async (msg) => {
    try {
      await axios.patch(`${API}/parent-communication/director/messages/${msg.id}/unarchive`);
      toast.success("Message restored to inbox");
      fetchMessages();
    } catch {
      toast.error("Unable to restore message");
    }
  };

  const clearAllArchived = async () => {
    if (!window.confirm(`Permanently delete all ${archived.length} archived messages? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/parent-communication/director/messages/archived/clear-all`);
      toast.success("All archived messages deleted");
      fetchMessages();
    } catch {
      toast.error("Unable to clear archived messages");
    }
  };

  const unread = messages.filter((m) => !m.read).length;

  return (
    <Card className="overflow-hidden border-2 border-violet-100 bg-white shadow-xl" data-testid="parent-director-inbox-card">
      <CardHeader className="bg-gradient-to-r from-violet-50 to-blue-50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-violet-800">
            <MessageSquare className="h-5 w-5" />
            Parent Messages
            {unread > 0 && <Badge className="bg-violet-600 text-white text-xs">{unread} new</Badge>}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={fetchMessages} disabled={loading} data-testid="parent-director-inbox-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        {/* Tabs */}
        <div className="flex gap-1 mt-3">
          <button
            onClick={() => setActiveTab("active")}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${activeTab === "active" ? "bg-violet-600 text-white" : "text-violet-700 hover:bg-violet-100"}`}
            data-testid="inbox-tab-active"
          >
            Inbox {messages.length > 0 && <span className="ml-1 text-xs opacity-80">({messages.length})</span>}
          </button>
          <button
            onClick={() => setActiveTab("archived")}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${activeTab === "archived" ? "bg-red-600 text-white" : "text-red-600 hover:bg-red-50"}`}
            data-testid="inbox-tab-archived"
          >
            Archived {archived.length > 0 && <span className="ml-1 text-xs opacity-80">({archived.length})</span>}
          </button>
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-4 max-h-[520px] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8"><div className="h-7 w-7 animate-spin rounded-full border-4 border-violet-500 border-t-transparent" /></div>
        ) : activeTab === "archived" ? (
          <ArchivedMessagesTab
            messages={archived}
            onRestore={restoreMessage}
            onDelete={deleteMessage}
            onClearAll={clearAllArchived}
          />
        ) : messages.length === 0 ? (
          <div className="text-center py-10 text-slate-400" data-testid="parent-director-inbox-empty">
            <MessageSquare className="mx-auto h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm">No parent messages yet.</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`director-message-${msg.id}`}>
              {/* Header */}
              <div className="flex items-center justify-between bg-slate-50 px-4 py-2 border-b border-slate-200">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{msg.parentName}</p>
                  {msg.childNames?.length > 0 && <p className="text-xs text-slate-500">re: {msg.childNames.join(", ")}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-slate-400">{(() => { const dt = new Date(msg.createdAt || msg.sentAt); return isNaN(dt.getTime()) ? "" : dt.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); })()}</p>
                  <Button variant="ghost" size="sm" onClick={() => archiveMessage(msg)} title="Archive message" className="h-7 w-7 p-0 text-slate-400 hover:text-amber-600 hover:bg-amber-50" data-testid={`archive-message-${msg.id}`}>
                    <Archive className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMessage(msg)} title="Delete message" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-message-${msg.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {/* Parent message */}
              <div className="px-4 py-3 bg-emerald-50">
                <p className="text-sm text-slate-700">{msg.message}</p>
              </div>
              {/* Existing replies */}
              {msg.replies?.map((reply, i) => (
                <div key={i} className="px-4 py-3 bg-blue-50 border-t border-slate-100">
                  <p className="text-xs font-semibold text-blue-700 mb-1">{reply.from} replied</p>
                  <p className="text-sm text-slate-700">{reply.message}</p>
                </div>
              ))}
              {/* Reply box */}
              <div className="px-4 py-3 border-t border-slate-200 space-y-2">
                <Textarea
                  placeholder="Type a reply..."
                  rows={2}
                  value={replyText[msg.id] || ""}
                  onChange={(e) => setReplyText((prev) => ({ ...prev, [msg.id]: e.target.value }))}
                  className="text-sm border-slate-300"
                  data-testid={`director-reply-input-${msg.id}`}
                />
                <Button size="sm" onClick={() => sendReply(msg.id)} disabled={replying === msg.id || !replyText[msg.id]?.trim()} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid={`director-reply-send-${msg.id}`}>
                  {replying === msg.id ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Sending...</> : <><Send className="mr-1 h-3 w-3" /> Reply</>}
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};
